import React, { useEffect, useRef } from 'react';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import PostAddRoundedIcon from '@material-ui/icons/PostAddRounded';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import PhoneAndroidRoundedIcon from '@material-ui/icons/PhoneAndroidRounded';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ComputerRoundedIcon from '@material-ui/icons/ComputerRounded';
import LoopRoundedIcon from '@material-ui/icons/LoopRounded';
import HomeWorkOutlinedIcon from '@material-ui/icons/HomeWorkOutlined';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import LocationOnRoundedIcon from '@material-ui/icons/LocationOnRounded';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import NotificationsActiveOutlinedIcon from '@material-ui/icons/NotificationsActiveOutlined';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';
import Navbar from '../../components/navBar/navBar';
import PageContainer from '../../components/PageContainer/PageContainer';
import { verifyLogin } from '../../helpers';
import { GUIDES } from './helpContent';
import '../../assets/style/detailSection.scss';
import '../../assets/style/help.scss';
import '../../assets/style/workflow.scss';

// "How to disburse a loan through BrickEZ": the whole lifecycle on one scrolling page (from the
// old per-subscriber SOP, made generic). Each step has a short text and an
// illustrated mock-up that animates once when it scrolls into view. The
// mock-ups are drawings, not live data.

// `videos`: Help guide ids (helpContent.js) shown as "Watch how" links.
const STEPS = [
  {
    id: 'search', title: 'Search for the loan', short: 'Loans', Icon: SearchRoundedIcon, videos: [],
  },
  {
    id: 'add', title: 'Add the loan', short: 'If it isn\'t there', Icon: PostAddRoundedIcon, videos: ['add-loan', 'update-gps'],
  },
  {
    id: 'plan', title: 'Generate the plan', short: 'Matches floors', Icon: AccountTreeOutlinedIcon, videos: ['generate-plan'],
  },
  {
    id: 'progress', title: 'Record past progress', short: 'Verify completed work', Icon: VerifiedUserOutlinedIcon, videos: ['verify-progress'],
  },
  {
    id: 'disbursed', title: 'Record past disbursements', short: 'With stage %', Icon: AccountBalanceWalletOutlinedIcon, videos: [],
  },
  {
    id: 'request', title: 'Progress & request', short: 'Customer app → verify', Icon: PhoneAndroidRoundedIcon, app: true, videos: ['verify-progress'],
  },
  {
    id: 'reports', title: 'Reports & release', short: 'Download, review, notify', Icon: DescriptionOutlinedIcon, videos: ['progress-report', 'drf'],
  },
];

// "Watch how" links for a step: only guides that exist and have a video.
const watchLinks = (ids) => ids
  .map((id) => GUIDES.find((g) => g.id === id && g.video))
  .filter(Boolean);

// Illustrated demo "site photos": 0 foundation, 1 walls, 2 roof slab, 3 plastered.
function SitePhoto({ kind }) {
  const bricks = (x, y, w, h) => {
    const lines = [];
    for (let r = 0; r * 6 < h; r += 1) {
      lines.push(<line key={`h${r}`} x1={x} y1={y + r * 6} x2={x + w} y2={y + r * 6} stroke='#E9A27E' strokeWidth='1' />);
      for (let c = (r % 2) * 6; c < w; c += 12) {
        lines.push(<line key={`v${r}-${c}`} x1={x + c} y1={y + r * 6} x2={x + c} y2={y + r * 6 + 6} stroke='#E9A27E' strokeWidth='1' />);
      }
    }
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} fill='#C8643C' />
        {lines}
      </g>
    );
  };
  const posts = (xs, y, h) => xs.map((x) => <rect key={x} x={x} y={y} width='3' height={h} fill='#6B4E3A' />);
  return (
    <svg viewBox='0 0 120 120' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>
      <defs>
        <linearGradient id='wf-sky' x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0' stopColor='#9CC7F2' />
          <stop offset='1' stopColor='#DDEBF8' />
        </linearGradient>
      </defs>
      <rect width='120' height='120' fill='url(#wf-sky)' />
      <circle cx='98' cy='22' r='9' fill='#FFE08A' />
      <rect y='92' width='120' height='28' fill='#B98B5E' />
      <rect y='92' width='120' height='4' fill='#8E6A45' />
      {kind === 0 ? (
        <g>
          <rect x='18' y='82' width='84' height='14' fill='#9A9FA8' />
          {posts([26, 42, 58, 74, 90], 58, 36)}
          <rect x='20' y='60' width='80' height='2' fill='#6B4E3A' />
        </g>
      ) : null}
      {kind === 1 ? (
        <g>
          {bricks(20, 46, 80, 48)}
          <rect x='46' y='62' width='16' height='32' fill='#5B4636' />
          <rect x='72' y='58' width='16' height='14' fill='#7FB0D8' />
          <rect x='14' y='30' width='3' height='64' fill='#8A8F99' />
          <rect x='106' y='30' width='3' height='64' fill='#8A8F99' />
          <rect x='14' y='42' width='95' height='3' fill='#8A8F99' />
        </g>
      ) : null}
      {kind === 2 ? (
        <g>
          {bricks(20, 50, 80, 44)}
          <rect x='14' y='40' width='92' height='10' fill='#A3A8B0' />
          {posts([22, 40, 58, 76, 94], 50, 44)}
        </g>
      ) : null}
      {kind === 3 ? (
        <g>
          <rect x='20' y='46' width='80' height='48' fill='#F1E3C8' />
          <rect x='14' y='36' width='92' height='10' fill='#A3A8B0' />
          <rect x='46' y='62' width='16' height='32' fill='#8C6A4E' />
          <rect x='72' y='58' width='16' height='14' fill='#7FB0D8' />
        </g>
      ) : null}
    </svg>
  );
}

// ---------- mock-ups, one per step ----------
const Mock = ({
  icon: Icon, title, action, children,
}) => (
  <div className='wf-mock'>
    <div className='wf-mhead'>
      {Icon ? <Icon /> : null}
      <span>{title}</span>
      {action || null}
    </div>
    {children}
  </div>
);

const MOCKS = {
  search: () => (
    <Mock
      icon={null}
      title='Loans'
      action={(
        <span className='wf-search'>
          <SearchRoundedIcon />
          <span className='wf-typed'>HL-2026-0142</span>
        </span>
      )}
    >
      <table className='wf-t'>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Loan number</th>
            <th>Branch</th>
          </tr>
        </thead>
      </table>
      <div className='wf-empty wf-late'>No loans found — add it as a new loan</div>
    </Mock>
  ),
  add: () => (
    <Mock icon={PostAddRoundedIcon} title='Add loan' action={<span className='wf-btn'>Review</span>}>
      <div className='wf-mbody'>
        <div className='wf-sect'>
          <HomeWorkOutlinedIcon />
          Property
        </div>
        <div className='wf-fields'>
          <div className='wf-f d1'>
            <span>Structure type *</span>
            <div>RCC Frame</div>
          </div>
          <div className='wf-f d2'>
            <span>No of floors *</span>
            <div>GROUND +1</div>
          </div>
          <div className='wf-gps'>
            <div className='wf-f d3'>
              <span>Latitude</span>
              <div>10.826245</div>
            </div>
            <div className='wf-f d4'>
              <span>Longitude</span>
              <div>78.685712</div>
            </div>
          </div>
        </div>
      </div>
    </Mock>
  ),
  plan: () => (
    <Mock icon={AccountTreeOutlinedIcon} title='Choose construction plan'>
      <div className='wf-mbody wf-stack'>
        {[['Load Bearing (Ground)', 'Ground floor', false], ['RCC Frame (Ground +1)', 'Two floors', true], ['RCC Frame (Ground +2)', 'Three floors', false]].map(([name, sub, sel]) => (
          <div key={name} className={`wf-plan${sel ? ' sel' : ''}`}>
            <div>
              <b>{name}</b>
              <small>{sub}</small>
            </div>
            <CheckCircleRoundedIcon className='wf-ck' />
          </div>
        ))}
      </div>
    </Mock>
  ),
  progress: () => (
    <Mock icon={VerifiedUserOutlinedIcon} title='Construction plan'>
      <table className='wf-t'>
        <thead>
          <tr>
            <th>Activity</th>
            <th>Weightage</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {[['Foundation', '20%', true], ['Plinth beam', '10%', true], ['Brick work – lintel', '10%'], ['Roof slab', '20%'], ['Plastering', '20%']].map(([a, w, done], i) => (
            <tr key={a}>
              <td>{a}</td>
              <td>{w}</td>
              <td>
                <span className={`wf-status${done ? ` flip d${i + 1}` : ''}`}>
                  <span className='before'>Yet to start</span>
                  {done ? <span className='after'>Verified</span> : null}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='wf-mbody'>
        <div className='wf-meter'>
          Verified
          <span className='wf-bar'><i /></span>
          <b>30%</b>
        </div>
      </div>
    </Mock>
  ),
  disbursed: () => (
    <Mock icon={AccountBalanceWalletOutlinedIcon} title='Disbursements' action={<span className='wf-btn out'>+ Add Disbursement</span>}>
      <table className='wf-t'>
        <thead>
          <tr>
            <th>Date</th>
            <th>Amount</th>
            <th>Stage %</th>
          </tr>
        </thead>
        <tbody>
          <tr className='wf-row d1'>
            <td>01-08-2026</td>
            <td>₹ 5,00,000</td>
            <td>20%</td>
          </tr>
          <tr className='wf-row d2'>
            <td>01-09-2026</td>
            <td>₹ 7,50,000</td>
            <td>30%</td>
          </tr>
          <tr className='wf-row d3 total'>
            <td>Total</td>
            <td>₹ 12,50,000</td>
            <td>50%</td>
          </tr>
        </tbody>
      </table>
    </Mock>
  ),
  request: () => (
    <div className='wf-duo'>
      <div className='wf-phone'>
        <div className='wf-screen'>
          <div className='wf-pbar'>
            <b>Brick work – lintel</b>
            <small>Upload completion photos</small>
          </div>
          <div className='wf-pgrid'>
            {[0, 1, 2, 3].map((k) => <div key={k} className={`wf-ph d${k + 1}`}><SitePhoto kind={k} /></div>)}
          </div>
          <div className='wf-geo'>
            <LocationOnRoundedIcon />
            At the property location
          </div>
          <div className='wf-preq'>
            <span className='before'>Request disbursement</span>
            <span className='after'>Request sent ✓</span>
          </div>
        </div>
      </div>
      <ArrowForwardRoundedIcon className='wf-arrow' />
      <div className='wf-mock wf-webcard'>
        <div className='wf-mhead'>
          <ComputerRoundedIcon />
          <span>Brick work – lintel</span>
        </div>
        <div className='wf-mbody wf-stack'>
          <div>
            Status
            {' '}
            <span className='wf-status sub flip d6'>
              <span className='before'>Submitted</span>
              <span className='after'>Verified</span>
            </span>
          </div>
          <span className='wf-btn'>
            <CheckCircleRoundedIcon />
            Verify
          </span>
        </div>
      </div>
    </div>
  ),
  reports: () => (
    <div className='wf-col'>
      <div className='wf-docs'>
        <div className='wf-doc d1'>
          <span className='wf-dl'><GetAppRoundedIcon /></span>
          <h4>Progress report</h4>
          <div className='ln' />
          <div className='ln s' />
          <div className='wf-thumbs'>{[1, 2, 3].map((k) => <div key={k}><SitePhoto kind={k} /></div>)}</div>
          <div className='wf-remark'>BCM remarks: work as per plan.</div>
        </div>
        <div className='wf-doc d2'>
          <span className='wf-dl'><GetAppRoundedIcon /></span>
          <h4>Disbursement Request Form</h4>
          <div className='ln' />
          <div className='ln' />
          <div className='ln s' />
          <div className='ln' />
          <div className='ln s' />
        </div>
      </div>
      <div className='wf-notify d3'>
        <NotificationsActiveOutlinedIcon />
        Request status:
        <span className='wf-status flip d4'>
          <span className='before'>Open</span>
          <span className='after'>Disbursed</span>
        </span>
        <small>customer notified</small>
      </div>
    </div>
  ),
};

const TEXT = {
  search: [
    'Open **Loans** and search by loan number or customer name.',
    'Loans already in BrickEZ show up here.',
    'Not found? Add it in the next step.',
  ],
  add: [
    'Click **Add Loan** and fill in the customer, loan and property details.',
    'Pick the **structure type** and **number of floors** — they decide the construction plan.',
    'Enter **latitude and longitude** from the last valuation report, in decimals (for example 10.826245). Customers can upload photos only at this location.',
  ],
  plan: [
    'On the loan, open **Construction Plan** and click **Generate plan**.',
    'Choose the plan that matches the structure type and number of floors.',
    'The plan\'s activities and their weightage appear on the loan.',
  ],
  progress: [
    'Open each activity already completed in the last valuation report and change its status to **Verified**.',
    'Add the remark “Already completed and verified”.',
    'For buildings with more than one floor, pick the floor at the top of the activity and verify floor by floor.',
  ],
  disbursed: [
    'In **Disbursements**, click **Add Disbursement** for each earlier disbursement: date, amount and **stage %**.',
    'The stage % follows your internal working and appears in the Progress report.',
  ],
  request: [
    'The customer signs in to the BrickEZ app with an **OTP** sent to the mobile number on the loan.',
    'For each activity they upload the photos and videos asked for — accepted only **at the property\'s location**.',
    'They request the next disbursement. You open the submitted activities and **verify** (or reject) them.',
  ],
  reports: [
    'On the loan, download the **Progress report** (choose up to 6 photos) and, on the request, the **Disbursement Request Form**.',
    'The BCM reviews both and adds remarks in the Progress report.',
    'After releasing the amount, change the request\'s status — the customer gets a notification.',
  ],
};

const rich = (text) => text.split(/\*\*(.+?)\*\*/g)
  // eslint-disable-next-line react/no-array-index-key
  .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

export default function DisbursementProcess(props) {
  verifyLogin();
  const root = useRef(null);

  // Play each step's animation once, when it scrolls into view.
  useEffect(() => {
    const els = root.current ? [...root.current.querySelectorAll('.wf-anim')] : [];
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.35 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jump = (id) => {
    const el = document.getElementById(`step-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className='ds-page'>
      <Navbar MainProps={props} />
      <PageContainer width='narrow' stack>
        <div ref={root} className='wf'>
          <a className='help-back' href='/help'>
            <ChevronLeftRoundedIcon />
            Help
          </a>

          <section className='ds-card wf-hero wf-anim'>
            <h1>How to disburse a loan through BrickEZ</h1>
            <p className='help-muted'>The whole process in 7 steps, from adding the loan to releasing each tranche.</p>
            <a className='help-action wf-watch' href='/help#disbursement-process'>
              <PlayArrowRoundedIcon />
              Watch the video (1:20)
            </a>
            <div className='wf-legend'>
              <span className='wf-chip web'>
                <ComputerRoundedIcon />
                Web dashboard (branch team)
              </span>
              <span className='wf-chip app'>
                <PhoneAndroidRoundedIcon />
                Customer app
              </span>
            </div>
            <div className='wf-flow'>
              <div className='wf-rail'><i /></div>
              <div className='wf-bracket top'><span>Once per loan</span></div>
              <div className='wf-bracket low'>
                <span>
                  <LoopRoundedIcon />
                  Every stage
                </span>
              </div>
              {STEPS.map((s, i) => (
                <button type='button' key={s.id} className={`wf-node${s.app ? ' app' : ''} d${i + 1}`} onClick={() => jump(s.id)}>
                  <span className='wf-bubble'><s.Icon /></span>
                  <span className='wf-num'>{i + 1}</span>
                  <b>{s.title}</b>
                  <small>{s.short}</small>
                </button>
              ))}
            </div>
          </section>

          {STEPS.map((s, i) => {
            const M = MOCKS[s.id];
            return (
              <section key={s.id} id={`step-${s.id}`} className={`ds-card wf-step wf-anim${s.app ? ' app' : ''}`}>
                <div className='wf-text'>
                  <div className='wf-stepno'>{`Step ${i + 1} of 7`}</div>
                  <h2>{s.title}</h2>
                  <span className={`wf-chip ${s.app ? 'app' : 'web'}`}>
                    {s.app ? <PhoneAndroidRoundedIcon /> : <ComputerRoundedIcon />}
                    {s.app ? 'Customer app + web dashboard' : 'Web dashboard'}
                  </span>
                  <ul>{TEXT[s.id].map((t) => <li key={t}>{rich(t)}</li>)}</ul>
                  {watchLinks(s.videos).length ? (
                    <div className='wf-watchlinks'>
                      {watchLinks(s.videos).map((g) => (
                        <a key={g.id} className='wf-watchlink' href={`/help#${g.id}`}>
                          <PlayArrowRoundedIcon />
                          <span>
                            Watch how:
                            {' '}
                            <b>{g.title}</b>
                          </span>
                          <small>{g.video.duration}</small>
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className='wf-visual'><M /></div>
              </section>
            );
          })}

          <section className='wf-end'>
            <div className='wf-loop'>
              <span>6</span>
              <LoopRoundedIcon />
              <span>7</span>
            </div>
            <h2>Steps 6 and 7 repeat for each stage</h2>
            <p>until the loan is fully disbursed.</p>
          </section>
        </div>
      </PageContainer>
    </div>
  );
}
